import { useQuery } from '@tanstack/react-query'
import { getQueryFn } from '@/lib/queryClient'
import {PricingData} from "@shared/interfaces/pricingData.interface.ts";

type InvestmentBreakdownReturn = {
    platformCost: number;
    pillarCost: number;
    pillarCostPerUnit: number | string; // matches your current return value
    entityCost: number;
    entityCount: number;
    tieredUnitPrice: number;
    annualSubscription: number;
    servicesCost: number;
    totalInvestment: number;
    fiveYearEntityCost: number;
    yearlyBreakdown: {
        year: number;
        platformCost: number;
        pillarCost: number;
        entityCost: number;
        servicesCost: number;
        total: number;
    }[];
    pillarCostsByYear: Record<number, number>;
};

export type CalculateInvestmentBreakdownFn = (
    revenueBand: string,
    selectedPillars: string[],
    entitiesCount: number,
    pricingData?: PricingData
) => InvestmentBreakdownReturn | null;

export async function fetchPricingData(baseUrl: string = ''): Promise<PricingData> {
    let url: string;

    if (baseUrl) {
        const normalized = baseUrl.replace(/\/$/, '');
        url = `${normalized}/api/pricing-data`;
    } else if (typeof window !== 'undefined' && typeof window.location !== 'undefined') {
        // Browser use-case (hooks in components): relative URL is fine
        url = '/api/pricing-data';
    } else {
        // Node (no window): must provide absolute baseUrl
        throw new Error(
            'fetchPricingData: baseUrl is required when called from Node. ' +
            'Pass an absolute URL like http://localhost:5000'
        );
    }

    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to fetch pricing data: ${response.status}`);
    }
    const data = await response.json();
    return data as PricingData;
}

export function usePricingData() {
  return useQuery<PricingData>({
    queryKey: ['/api/pricing-data'],
    // Use shared queryFn so Authorization header is attached
    queryFn: getQueryFn({ on401: 'throw' }),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 3,
    retryDelay: 1000
  })
}


export function calculateInvestmentBreakdown(
  revenueBand: string,
  selectedPillars: string[],
  entitiesCount: number,
  pricingData?: PricingData
) {
  if (!pricingData) {
    console.warn('❌ Pricing data not available - using fallback calculations')
    return null
  }

  // Get platform cost - NO HARDCODED FALLBACKS
  const platformEntry = pricingData.platform.find(p => p.revenue_band === revenueBand)
  const platformCost = platformEntry ? Number(platformEntry.annual_price_usd) : 0
  
  if (!platformEntry) {
    console.error(`[PRICING] CRITICAL: No platform pricing found for revenue band: ${revenueBand}`)
    console.error('[PRICING] All pricing must come from database - no hardcoded fallbacks allowed')
  }

  // Get pillar cost - FIXED: Calculate cost for ALL selected pillars, not just first one
  let pillarCost = 0
  selectedPillars.forEach(pillarName => {
    const pillarEntry = pricingData.pillars.find(p => 
      p.revenue_band === revenueBand && p.pillar_name === pillarName
    )
    const pillarCostForThisPillar = pillarEntry ? Number(pillarEntry.annual_price_usd) : 0
    
    if (!pillarEntry) {
      console.error(`[PRICING] CRITICAL: No pillar pricing found for ${pillarName} at revenue band: ${revenueBand}`)
      console.error('[PRICING] All pricing must come from database - no hardcoded fallbacks allowed')
    }
    pillarCost += pillarCostForThisPillar
    
  })
  

  // Calculate entity cost with volume-based tiered pricing
    const entityTier = pricingData.entities.find(tier =>
        entitiesCount >= tier.min_entities &&
        (tier.max_entities === null || entitiesCount <= tier.max_entities)
    );
  
  // Get entity base price from platform pricing table - NO HARDCODED VALUES
  const entityBasePriceEntry = pricingData.platform.find(p => p.revenue_band === revenueBand)
  const basePricePerEntity = entityBasePriceEntry?.entity_base_price ? Number(entityBasePriceEntry.entity_base_price) : 7500
  
  if (!entityTier) {
    console.error(`[PRICING] CRITICAL: No entity tier found for ${entitiesCount} entities`)
    console.error('[PRICING] All pricing must come from database - no hardcoded fallbacks allowed')
  }
  const priceModifier = entityTier ? Number(entityTier.price_modifier) : 1.0
  
  // Apply tiered pricing logic
  const tieredUnitPrice = basePricePerEntity * priceModifier
  const entityCostPerYear = tieredUnitPrice * entitiesCount

  const annualSubscription = platformCost + pillarCost + entityCostPerYear
  const servicesCost = annualSubscription * 2 // 2x first year

  // Calculate yearly breakdown for Investment Breakdown table
  const yearlyBreakdown = [2025, 2026, 2027, 2028, 2029].map((year, index) => ({
    year,
    platformCost,
    pillarCost,
    entityCost: entityCostPerYear,
    servicesCost: index === 0 ? servicesCost : 0,
    total: annualSubscription + (index === 0 ? servicesCost : 0)
  }))

  // Create pillar costs by year object with number keys for table rendering
  const pillarCostsByYear: Record<number, number> = {
    2025: pillarCost,
    2026: pillarCost,
    2027: pillarCost,
    2028: pillarCost,
    2029: pillarCost
  }

  // Get unit price for debug logging
  const samplePillarPrice = pricingData.pillars.find(p => 
    p.revenue_band === revenueBand && selectedPillars.includes(p.pillar_name)
  )?.annual_price_usd || 0


  return {
    platformCost,
    pillarCost,
    pillarCostPerUnit: samplePillarPrice,
    entityCost: entityCostPerYear,
    entityCount: entitiesCount,
    tieredUnitPrice,
    annualSubscription,
    servicesCost,
    totalInvestment: (annualSubscription * 5) + servicesCost,
    fiveYearEntityCost: entityCostPerYear * 5,
    yearlyBreakdown,
    pillarCostsByYear
  }
}
